import { useEffect, useState } from 'react';
import {
  ExerciseStatus,
  IAlternative,
  INewExercise,
} from '../../../models/new_exercise';
import { copyText } from '../../../utils';
import { getDisciplineName } from '../../../utils/discipline';
import { getVideoConfig } from '../../../utils/video';
import { Alert } from '../../atoms/Alert';
import { Button } from '../../atoms/Button';
import { Chip } from '../../atoms/Chip';
import { Icon } from '../../atoms/Icon';
import { AccuracyDifficulty } from '../../molecules/AccuracyDifficulty';
import ExerciseAlternative from '../../molecules/ExerciseAlternative';
import InnerHTMLContainer from '../../molecules/InnerHTMLContainer';
import Player from '../../molecules/Player';
import { Typography } from '../../molecules/Typography';
import { Modal } from '../../templates/Modal';
import { ButtonGroup } from '../ButtonGroup';
import Comment from '../comment';
import { ContentActions } from '../ContentActions';
import { BadgeScore } from '../../atoms/BadgeScore';
import useGetBadgeScores from '../../../../modules/Disciplines/hooks/useGetBadgeScores';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import { useShouldShowPopUpScore } from '../../../hooks/useShouldShowPopUpScore';

interface NewExerciseProps {
  exercise: INewExercise | null;
  originPlan?: boolean;
  onFavoriteChange?: (newValue: boolean) => void;
  showExercisePercentage?: boolean;
  showReportButton?: boolean;
  canTryAgain?: boolean;
  checkAlternative?: (alternative: IAlternative[]) => Promise<boolean>;
  isLoading?: boolean;
  getExerciseAnswer?: (exerciseId: string) => Promise<string[]>;
  getExerciseAnswerLoading?: boolean;
  discursiveAnswer?: () => void;
}

export function NewExercise({
  exercise,
  originPlan = false,
  onFavoriteChange,
  showExercisePercentage = false,
  showReportButton = false,
  canTryAgain = true,
  checkAlternative,
  isLoading,
  getExerciseAnswer,
  getExerciseAnswerLoading,
  discursiveAnswer,
}: NewExerciseProps) {
  const { user } = useUserStore();
  const isAdmin = user?.role?.includes('admin') ?? false;

  const [showVideo, setShowVideo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { shouldShowBadgeScore } = useShouldShowPopUpScore();

  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | undefined>();
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [alternatives, setAlternatives] = useState<IAlternative[]>(exercise?.alternatives || []);
  const [selectedAlternative, setSelectedAlternative] = useState<IAlternative[] | null>(null);

  const { getBadgeScores, badgeScore, isPending: isPendingBadgeScores } = useGetBadgeScores();

  useEffect(() => {
    if (user?.id) {
      getBadgeScores({
        externalId: user?.id,
        externalContent: exercise?.id,
        externalContentOrigin: 'plano-adaptativo',
      });
    }
  }, [exercise, user?.id]);

  const exerciseTypes = {
    single_answer: 'única',
    multiple_answer: 'de múltipla escolha',
    discursive: 'discursiva',
  };

  useEffect(() => {
    if (typeof window?.MathJax !== 'undefined') {
      window.MathJax.typesetPromise();
    }
  });

  if (!exercise) return <></>;

  const { playerType, token, hasToken } = getVideoConfig(
    exercise.resolution_video
  );

  useEffect(() => {
    setAlternatives(exercise.alternatives);
  }, [exercise.alternatives]);

  function getAlternativeStatus(alternative: IAlternative): ExerciseStatus {
    const isSelectedAlternative = selectedAlternative?.some(
      (it) => it._id === alternative._id
    );
    const isCorrectAlternative = alternative.correct;

    if (isSelectedAlternative && isCorrectAlternative && isAnswered)
      return 'success';

    if (!isSelectedAlternative && isCorrectAlternative && showCorrectAnswer)
      return 'correct';

    if (isSelectedAlternative && !isCorrectAlternative && isAnswered)
      return 'error';

    return 'default';
  }

  async function handleCheckAnswer() {
    if (!selectedAlternative) return;

    if (checkAlternative) {
      const isCorrect = await checkAlternative(selectedAlternative);

      setIsAnswered(true);
      setIsCorrect(isCorrect);
      setSelectedAlternative(
        selectedAlternative.map((it) => ({ ...it, correct: isCorrect }))
      );

      if (isCorrect) {
        setAlternatives((prevAlternatives) =>
          prevAlternatives.map((alternative) => ({
            ...alternative,
            correct: selectedAlternative.some(
              (it) => it._id === alternative._id
            ),
          }))
        );
        setShowCorrectAnswer(true);
      }

      if (user?.id) {
        getBadgeScores({
          externalId: user?.id,
          externalContent: exercise?.id,
          externalContentOrigin: 'plano-adaptativo',
        });
      }
      return;
    }

    setIsAnswered(true);
    setIsCorrect(selectedAlternative.filter((it) => it?.correct).length > 0);

    if (selectedAlternative.filter((it) => it?.correct).length > 0) {
      setShowCorrectAnswer(true);
    }
  }

  function handleTryAgain() {
    const hasAnyCorrectAlternativeOriginaly = exercise?.alternatives?.some(
      (alternative) => alternative.correct
    );

    if (!hasAnyCorrectAlternativeOriginaly) {
      setAlternatives(exercise?.alternatives || []);
    }

    setShowCorrectAnswer(false);
    setSelectedAlternative(null);
    setIsAnswered(false);
    setIsCorrect(undefined);
  }

  function handleSelectAlternative(alternative: IAlternative) {
    if (exercise?.exercise_type === 'multiple_answer') {
      if (selectedAlternative?.some((it) => it._id === alternative._id)) {
        const selectedAlternatives = [...(selectedAlternative || [])];
        selectedAlternatives.splice(
          selectedAlternatives.indexOf(alternative),
          1
        );

        setSelectedAlternative(selectedAlternatives);
        return;
      }

      setSelectedAlternative([...(selectedAlternative || []), alternative]);
      return;
    }

    if (selectedAlternative?.some((it) => it._id === alternative._id)) {
      setSelectedAlternative(null);
      return;
    }

    setSelectedAlternative([alternative]);
  }

  async function handleShowCorrectAnswer() {
    if (!exercise) return;
    const newValue = !showCorrectAnswer;

    const hasAnyCorrectAlternative = alternatives.some(
      (alternative) => alternative.correct
    );

    if (
      !hasAnyCorrectAlternative &&
      newValue &&
      getExerciseAnswer &&
      exercise.exercise_type !== 'discursive'
    ) {
      const correctAnswers = await getExerciseAnswer(exercise.id);
      const newAlternatives = exercise.alternatives?.map((alternative) => {
        return {
          ...alternative,
          correct: correctAnswers.includes(alternative._id),
        };
      });

      setAlternatives(newAlternatives);
      setShowCorrectAnswer(true);
      return;
    }

    setShowCorrectAnswer(newValue);

    if(originPlan && exercise.exercise_type === 'discursive') {
      discursiveAnswer?.();
    }
  }

  const shouldBadgeScore = shouldShowBadgeScore && !isPendingBadgeScores;

  useEffect(() => {
    if(exercise.userAnswer && exercise.userAnswer.length > 0) {
      const alternatives = exercise.alternatives.filter((alternative) => exercise.userAnswer?.includes(alternative._id));

      if(exercise.userAnswerCorrect) {
        setShowCorrectAnswer(true);
      }
      
      setSelectedAlternative(alternatives);
      setIsAnswered(true);
      setIsCorrect(exercise?.userAnswerCorrect ?? false);
    } else {
      setShowCorrectAnswer(false);
      setSelectedAlternative(null);
      setIsAnswered(false);
      setIsCorrect(undefined);
    }
  }, [exercise]);

  return (
    <div key={exercise.id} className='flex w-full flex-col gap-xxl'>
      <div className='flex flex-col gap-lg'>
        <div className='flex h-fit flex-col gap-sm'>
          <div className='flex items-center gap-lg'>
            {exercise.ancestors && exercise.ancestors.area && (
              <Typography size='small' className='text-mono-color-darker'>
                {getDisciplineName(exercise.ancestors.area.slug)}
              </Typography>
            )}
            {exercise.subjects && (
              <Typography size='small' className='text-mono-color-darker'>
                {exercise.subjects
                  .map((subject: { title: string }) => subject.title)
                  .join(', ')}
              </Typography>
            )}
          </div>
          {showExercisePercentage &&
            exercise.exercise_type !== 'discursive' && (
              <AccuracyDifficulty
                correctPercentage={exercise.correctPercentage}
              />
            )}
          <div className='flex flex-col justify-between gap-md md:flex-row md:items-center'>
            <div className='flex items-center gap-sm'>
              <Chip variant='negative'>{`Esta resposta é ${
                exerciseTypes[exercise.exercise_type]
              }`}</Chip>
              {shouldBadgeScore && (
                <BadgeScore
                  score={badgeScore?.pointAt ? badgeScore?.points : [1, 2]}
                  type={badgeScore?.didPoint ? 'success' : 'default'}
                />
              )}
            </div>

            <ContentActions.Root
              contentType={'Exercise'}
              contentId={exercise.id}
              className={'ml-auto'}
            >
              <ContentActions.FavoriteButton
                defaultFavorite={exercise.favorite}
                category={'exercise'}
                onChange={(isFavorite) => {
                  onFavoriteChange && onFavoriteChange(isFavorite);
                }}
              />
              {showReportButton && <ContentActions.Report />}
            </ContentActions.Root>
          </div>
        </div>
        <InnerHTMLContainer
          data={exercise.statement}
          type='courseProgram'
          className='hyphens-auto text-mono-color-darkest xs:break-keep'
        />
        <div className='flex items-center justify-between'>
          <Typography
            size='mini'
            className='w-fit cursor-pointer'
            onClick={() => copyText(exercise.id)}
          >
            Código: {exercise.id}
          </Typography>
          {isAdmin && (
            <Typography size='mini'>
              Gabarito: {(() => {
                const index = exercise.alternatives.findIndex((alt) => alt.correct);
                return index >= 0 ? String.fromCharCode(65 + index) : 'N/A';
              })()}
            </Typography>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-md rounded-lg bg-mono-color-lighter p-4 '>
        {exercise.exercise_type === 'discursive' ? (
          <>
            <Alert
              variant='warning'
              message='Desenvolva a questão em seu caderno e depois clique no botão VER RESPOSTA'
            />
            {showCorrectAnswer && (
              <div className='ml-10 rounded-md border-hairline border-mono-color-darkest p-5'>
                <InnerHTMLContainer
                  data={exercise.resolution}
                  type='courseProgram'
                  className='text-mono-color-darkest'
                />
              </div>
            )}
          </>
        ) : (
          alternatives.map((alternative) => {
            return (
              <div key={alternative._id}>
                {exercise.previouslyAnswered &&
                  getAlternativeStatus(alternative) === 'success' && (
                    <span className='flex w-fit items-center gap-xs whitespace-nowrap rounded-t-md bg-feedback-color-success-lightest px-3 py-1 text-sm font-medium text-feedback-color-success-darkest'>
                      <Icon
                        Icon='check_circle'
                        type='material'
                        className='text-base text-feedback-color-success-darkest'
                      />
                      Você já respondeu esta questão!
                    </span>
                  )}
                <ExerciseAlternative
                  alternativeId={alternative._id}
                  text={alternative.text}
                  value={alternative.text}
                  type={exercise.exercise_type}
                  selected={selectedAlternative?.some(
                    (it) => it._id === alternative._id
                  )}
                  status={getAlternativeStatus(alternative)}
                  disabled={isAnswered}
                  flushTopLeft={
                    exercise.previouslyAnswered &&
                    getAlternativeStatus(alternative) === 'success'
                  }
                  onSelected={() => handleSelectAlternative(alternative)}
                />
                {showCorrectAnswer && alternative?.correct && (
                  <div className='ml-5 rounded-md border-hairline border-mono-color-medium p-5 md:ml-10'>
                    <InnerHTMLContainer
                      data={exercise.resolution}
                      type='courseProgram'
                      className='text-mono-color-darkest'
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className='flex w-full'>
        <Button
          full
          onClick={handleCheckAnswer}
          disabled={!selectedAlternative || isAnswered}
          loading={isLoading}
        >
          Conferir
        </Button>
      </div>

      {isAnswered &&
        exercise.exercise_type !== 'discursive' &&
        (isCorrect ? (
          <Alert
            title='Resposta correta 😎'
            message='Parabéns!'
            variant='success'
          />
        ) : (
          <Alert
            title='Resposta incorreta 😔'
            message='Tente novamente ou veja a resposta correta'
            variant='error'
          />
        ))}

      {(isAnswered || exercise.exercise_type === 'discursive') && (
        <div className='flex flex-col items-center gap-md md:flex-row md:justify-between'>
          <Button
            allowReadOnly
            LeftIcon={showCorrectAnswer ? 'visibility_off' : 'visibility'}
            variant='text'
            onClick={handleShowCorrectAnswer}
            disabled={isCorrect}
            loading={getExerciseAnswerLoading}
          >
            {showCorrectAnswer ? 'Ocultar resposta' : 'Ver resposta'}
          </Button>
          {hasToken && (
            <Button
              LeftIcon='videocam'
              variant='outlined'
              onClick={() => {
                setShowVideo(true);
                setIsModalOpen(true);
              }}
            >
              Assistir resolução em vídeo
            </Button>
          )}
          {canTryAgain && (
            <Button
              LeftIcon='refresh'
              variant='text'
              disabled={exercise.exercise_type === 'discursive'}
              onClick={() => handleTryAgain()}
            >
              Tentar novamente
            </Button>
          )}
        </div>
      )}

      <Modal
        title='Resolução do exercício'
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <div className='flex flex-col gap-xl'>
          <ButtonGroup
            buttons={[
              {
                label: 'Resolução em vídeo',
                selected: showVideo,
                onClick: () => setShowVideo(true),
              },
              {
                label: 'Comentários',
                selected: !showVideo,
                onClick: () => setShowVideo(false),
              },
            ]}
          />

          {showVideo ? (
            <Player
              token={token}
              playerType={playerType}
              initialTime={0}
              className='h-[48vh] w-full'
            />
          ) : (
            <Comment
              title='Comentários da videoaula'
              targetId={exercise.id}
              targetType='Exercise'
              isModal
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
